import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { interval, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { EventService } from "../../event-service/event.service";
import { GroupService } from "../../models/group.service";
import { PageVisibilityService } from "../../page-visibility/page-visibility.service";
import { RememberService } from "../../remember/remember.service";

@Component({
  selector: "ab-group-poller",
  templateUrl: "./group-poller.component.html",
})
export class GroupPollerComponent implements OnChanges, OnDestroy, OnInit {
  @Input() public groups: any;
  @Input() public pageParams: any;

  @Output() public update = new EventEmitter<any>();

  public HTTP_STATUS_TOO_MANY_REQUESTS = 429;
  public POLL_INTERVAL = 30000;
  public groupLimit: boolean;
  public newGroupCount: number;
  public newNoticeCount: number;
  public noticeLimit: boolean;
  public pollInterval: Subscription;

  private MAX_GROUPS = 20;
  private MAX_NOTICES = 1000;
  private REMEMBER_KEY = "group-poller.disabled";
  private TITLE_REGEX = /^\(\d+\) /;
  private destroyed: boolean;
  private documentHidden: boolean;
  private documentHiddenSub: any;
  private documentVisibleSub: any;
  private seenGroups: any;
  private timeout: number;
  private updatesCount: number;

  constructor(
    private eventService: EventService,
    private groupService: GroupService,
    private pageVisibility: PageVisibilityService,
    private rememberService: RememberService,
  ) {
    this.seenGroups = {};
    this.destroyed = false;

    // Whether the dashboard is currently being viewed.
    this.documentHidden = false;

    // The sum of newGroupCount & newNoticeCount.
    this.updatesCount = 0;

    this.documentHiddenSub = this.eventService.subscribe(
      "documentHidden",
      () => (this.documentHidden = true),
    );

    this.documentVisibleSub = this.eventService.subscribe(
      "documentVisible",
      () => {
        this.documentHidden = false;
        if (!this.TITLE_REGEX.test(window.document.title)) {
          return;
        }

        this.refresh();
        this.updateTitleCounter(0);
      },
    );
  }

  public refresh() {
    this.update.emit({ highlight: true });
  }

  public ngOnInit() {
    this.pageVisibility.setUpListener();
  }

  public ngOnChanges() {
    if (!this.destroyed) {
      this.startPolling();
    }
  }

  public ngOnDestroy() {
    this.stopPolling();
    this.documentHiddenSub.unsubscribe();
    this.documentVisibleSub.unsubscribe();
    this.destroyed = true;
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
  }

  private startPolling() {
    this.stopPolling();
    this.newGroupCount = 0;
    this.newNoticeCount = 0;

    this.groupLimit = false;
    this.noticeLimit = false;

    this.seenGroups = {};
    for (const g of this.groups) {
      this.seenGroups[g.id] = g;
    }

    if (this.rememberService.get(this.REMEMBER_KEY)) {
      return;
    }

    this.pollInterval = interval(this.POLL_INTERVAL)
      .pipe(switchMap(() => this.groupService.query(this.pageParams)))
      .subscribe(
        (data: any) => {
          if (data == null || data.groups == null) {
            return;
          }
          this.processData(data.groups);

          const newUpdatesCount = this.newGroupCount + this.newNoticeCount;
          if (this.documentHidden && newUpdatesCount > this.updatesCount) {
            this.updateTitleCounter(newUpdatesCount);
          }
          this.updatesCount = newUpdatesCount;

          this.groupLimit = this.newGroupCount >= this.MAX_GROUPS;
          this.noticeLimit = this.newNoticeCount >= this.MAX_NOTICES;
          if (this.groupLimit || this.noticeLimit) {
            this.stopPolling();
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status === this.HTTP_STATUS_TOO_MANY_REQUESTS) {
            this.stopPolling();

            const waitTimeSec = Number(error.headers?.get("x-ratelimit-delay"));
            if (!waitTimeSec) {
              return;
            }
            this.timeout = window.setTimeout(() => {
              this.startPolling();
            }, waitTimeSec * 1000);
          }
        },
      );
  }

  private stopPolling() {
    if (this.pollInterval) {
      this.pollInterval.unsubscribe();
    }
  }

  private processData(groups: any[]) {
    for (const g of groups) {
      const seen = this.seenGroups[g.id];
      this.seenGroups[g.id] = g;

      if (seen) {
        this.newNoticeCount += g.noticeCount - seen.noticeCount;
        continue;
      }

      this.newGroupCount++;
    }
  }

  private updateTitleCounter(newUpdatesCount: number) {
    let { title } = window.document;
    if (this.TITLE_REGEX.test(title)) {
      title = title.split(this.TITLE_REGEX)[1];
    }

    return (window.document.title =
      newUpdatesCount > 0 ? `(${newUpdatesCount}) ${title}` : title);
  }
}
